import { useMemo } from "react";

import { useScopedDashboardPerfs } from "./DashboardPerfsProvider";
import { useDashboardPerfs } from "./useDashboardPerfs";
import type { CustomViewType, GetCustomViewsParams, SavedCustomView } from "./types";

interface UseCustomViewsResult {
  /** The user's saved views for this dashboard and table, newest last. `null`
   *  while the call is still outstanding — the tables keep their built-in
   *  "All metrics" view until the real list lands, rather than flashing an
   *  empty picker. */
  views: SavedCustomView[] | null;
  loading: boolean;
  error: string | null;
  /** Re-read the list — call after a save so a new view shows up without a
   *  page reload. */
  refetch: () => void;
}

/**
 * The saved custom views for one table.
 *
 * Inside a `DashboardPerfsProvider` (every Operating View dashboard) this is a
 * pure read of the response the page ALREADY fetched — the views arrive with
 * the dashboard's saved filters in a single `dashboard/custom-views` call, so a
 * table mounting later fires no request of its own. Outside one (All Metrics,
 * whose views are scoped to `@DashboardID = NULL`) the hook fetches for itself.
 *
 * `viewType` narrows the result to the table asking: the response carries every
 * view saved against the dashboard, By-Branch and By-Clinician alike.
 *
 * `params: null` keeps the hook idle — the caller passes null until it knows the
 * dashboard id.
 */
export function useCustomViews(
  params: GetCustomViewsParams | null,
  viewType: CustomViewType,
): UseCustomViewsResult {
  const shared = useScopedDashboardPerfs(params?.dashboardID ?? null);
  // Idle whenever the shared response already covers this scope — that call is
  // the one request per dashboard, and this hook must not duplicate it.
  const own = useDashboardPerfs(shared || !params ? null : params);
  const perfs = shared ?? own;

  const { views, loading, error, refetch } = perfs;
  // One dashboard's response covers several tables; keep this table's.
  const mine = useMemo(
    () => views?.filter((v) => v.details.viewType === viewType) ?? null,
    [views, viewType],
  );

  return { views: mine, loading, error, refetch };
}
